import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { useWsNavigate } from "../../hooks/useWsNavigate";
import { Sparkles, Search, MessageSquarePlus, Trash2, MessagesSquare } from "lucide-react";
import { discoveryAPI } from "../../services/api";
import { usePageTitle } from "../../contexts/PageTitleContext";
import PageHeader from "../../components/layout/PageHeader";
import BlockRenderer from "../../components/copilot/blocks/BlockRenderer";

const SOURCE_TYPE_OPTIONS = [
  { value: "", label: "All sources" },
  { value: "observation", label: "Observability calls" },
  { value: "scenario", label: "Scenario runs" },
];

export default function Discovery() {
  const { setPageTitle } = usePageTitle();
  const navigate = useWsNavigate();
  const { conversationId: urlConvId } = useParams(); // session id in the URL (/copilot/:id)

  // --- Chat thread state ---
  const [turns, setTurns] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);

  // --- Input + source scope ---
  const [question, setQuestion] = useState("");
  const [source, setSource] = useState(""); // "" (all) | "observation" | "scenario"
  // Co-pilot always runs in "auto" mode; the Data/Explore selector was removed.
  const [mode] = useState("auto");

  // --- Loading / error ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchParams] = useSearchParams();

  // Scroll-to-bottom ref for the thread
  const threadEndRef = useRef(null);

  useEffect(() => {
    setPageTitle("Co-Pilot");
    return () => setPageTitle(null);
  }, [setPageTitle]);

  // Load conversation list on mount
  useEffect(() => {
    loadConversations();
  }, []);

  // Scroll to bottom whenever turns change
  useEffect(() => {
    if (threadEndRef.current?.scrollIntoView) {
      threadEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [turns]);

  async function loadConversations() {
    try {
      const res = await discoveryAPI.listConversations();
      setConversations(res?.conversations || []);
    } catch { /* non-critical */ }
  }

  // Load the conversation named in the URL (deep link, refresh, or selection).
  async function loadConversationById(id) {
    try {
      const res = await discoveryAPI.getConversation(id);
      const messages = res?.messages || [];
      setTurns(messages.map((m) => (m.role === "user"
        ? { role: "user", question: m.content }
        : { role: "assistant", blocks: m.blocks || [] })));
      setConversationId(res?.id ?? Number(id));
      setError(null);
    } catch {
      navigate("/copilot", { replace: true }); // invalid/foreign id → fresh chat
    }
  }

  // Keep the loaded conversation in sync with the URL session id.
  useEffect(() => {
    if (urlConvId) {
      if (String(urlConvId) !== String(conversationId)) loadConversationById(urlConvId);
    } else if (conversationId !== null) {
      setTurns([]);
      setConversationId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlConvId]);

  async function runAsk(q) {
    if (!q?.trim()) return;
    const qTrimmed = q.trim();

    // Append user turn immediately
    setTurns((prev) => [...prev, { role: "user", question: qTrimmed }]);
    setLoading(true);
    setError(null);

    try {
      const res = await discoveryAPI.ask({
        question: qTrimmed,
        mode,
        conversationId: conversationId || undefined,
        source: source || undefined, // observability calls vs scenario runs
      });

      // Update conversationId from response; reflect a NEW conversation in the URL
      if (res?.conversation_id) {
        const wasNew = !conversationId;
        setConversationId(res.conversation_id);
        if (wasNew) navigate(`/copilot/${res.conversation_id}`, { replace: true });
      }

      // Append assistant turn
      setTurns((prev) => [...prev, { role: "assistant", blocks: res?.blocks || [] }]);

      // Refresh conversation list
      loadConversations();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to get answer");
      // Remove the user turn we optimistically added if ask failed
      setTurns((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  }

  // Auto-run question from URL param (?q=...)
  const autoRanRef = useRef(false);
  useEffect(() => {
    const q = searchParams.get("q");
    if (q && !autoRanRef.current) {
      autoRanRef.current = true;
      const src = searchParams.get("source") || "";
      setQuestion(q);
      if (src) setSource(src);
      setTimeout(() => {
        runAsk(q.trim());
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  async function handleAsk(e) {
    e?.preventDefault();
    if (!question.trim() || loading) return;
    const q = question.trim();
    setQuestion("");
    await runAsk(q);
  }

  function handleSelectConversation(conv) {
    // Navigate to the session URL; the sync effect loads it.
    navigate(`/copilot/${conv.id}`);
  }

  async function handleDeleteConversation(e, convId) {
    e.stopPropagation();
    try {
      await discoveryAPI.deleteConversation(convId);
      // If we're viewing the deleted conversation, start a new chat
      if (convId === conversationId) {
        handleNewChat();
      }
      loadConversations();
    } catch { /* silently fail */ }
  }

  function handleNewChat() {
    setTurns([]);
    setConversationId(null);
    setError(null);
    setQuestion("");
    navigate("/copilot");
  }

  const exampleQuestions = [
    { icon: "⚡", text: "Which calls had the highest average latency?" },
    { icon: "🙋", text: "Show me calls where the user interrupted the AI most" },
    { icon: "🔇", text: "Which calls had the most silence?" },
    { icon: "📞", text: "Which calls were ended by the customer?" },
  ];

  return (
    <div className="px-4 space-y-4">
      <PageHeader description="Ask natural language questions about your call data." />

      <div className="flex h-[calc(100vh-180px)] rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        {/* Conversations sidebar */}
        <div className="w-60 flex-shrink-0 flex flex-col gap-1 overflow-y-auto bg-muted/30 border-r border-border p-3">
          <button
            onClick={handleNewChat}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-border bg-card text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            <MessageSquarePlus className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            New chat
          </button>

          {/* Active (unsaved) chat indicator — shows "New chat" until the first
              message creates a titled conversation. */}
          {conversationId === null && (
            <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-primary/10 text-foreground">
              <MessagesSquare className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.5} />
              <span className="truncate flex-1 leading-snug">New chat</span>
            </div>
          )}

          {conversations.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-medium text-muted-foreground px-1 mb-1">Recent</p>
              <div className="flex flex-col gap-0.5">
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv)}
                    className={`group relative flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm transition-colors ${
                      conv.id === conversationId
                        ? "bg-primary/10 text-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    <MessagesSquare className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="truncate flex-1 leading-snug">{conv.title || "Conversation"}</span>
                    <button
                      onClick={(e) => handleDeleteConversation(e, conv.id)}
                      className="opacity-0 group-hover:opacity-100 flex-shrink-0 p-0.5 rounded text-muted-foreground hover:text-red-500 transition-all"
                      title="Delete conversation"
                    >
                      <Trash2 className="h-3 w-3" strokeWidth={1.75} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main chat area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden px-5 py-4">
          {/* Thread */}
          <div className="flex-1 overflow-y-auto space-y-4 pb-4 pr-1">
            {/* Turns */}
            {turns.map((turn, idx) => {
              if (turn.role === "user") {
                return (
                  <div key={idx} className="flex justify-end">
                    <div className="max-w-[90%] px-4 py-2.5 rounded-2xl rounded-br-md bg-primary text-primary-foreground text-sm leading-relaxed shadow-sm">
                      {turn.question}
                    </div>
                  </div>
                );
              }
              // assistant turn
              return (
                <div key={idx} className="flex items-start gap-3 max-w-[90%]">
                  <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 ring-1 ring-primary/15">
                    <Sparkles className="h-4 w-4 text-primary" strokeWidth={1.75} />
                  </span>
                  <div className="min-w-0 flex-1 space-y-3 pt-1">
                    <BlockRenderer
                      blocks={turn.blocks || []}
                      onClarify={(opt) => runAsk(`${turns[idx - 1]?.question || question} — ${opt}`)}
                      onCitationClick={(it) => {
                        if (it.call_observation_id) navigate(`/observability/conversations/${it.call_observation_id}`);
                      }}
                    />
                  </div>
                </div>
              );
            })}

            {/* Loading skeleton */}
            {loading && (
              <div className="flex items-start gap-3 max-w-[90%]">
                <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 ring-1 ring-primary/15">
                  <Sparkles className="h-4 w-4 text-primary animate-pulse" strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1 rounded-2xl rounded-tl-md border border-border bg-card p-4 space-y-3 animate-pulse shadow-sm">
                  <div className="h-3 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-full" />
                  <div className="h-3 bg-muted rounded w-5/6" />
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div ref={threadEndRef} />
          </div>

          {/* Search + filters — pinned at bottom */}
          <form onSubmit={handleAsk} className="space-y-3 pt-3">
            {/* Example questions — near the input, empty state only */}
            {turns.length === 0 && !loading && (
              <div className="flex flex-wrap gap-2">
                <span className="text-xs font-medium text-muted-foreground self-center mr-1">Try asking</span>
                {exampleQuestions.map(({ icon, text }) => (
                  <button
                    key={text}
                    type="button"
                    onClick={() => setQuestion(text)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card text-xs text-muted-foreground hover:border-input hover:text-foreground transition-colors"
                  >
                    <span className="leading-none">{icon}</span>
                    {text}
                  </button>
                ))}
              </div>
            )}

            {/* Search bar row */}
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <input
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ask anything about your calls..."
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
              {/* Data source scope */}
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                title="Data source"
                className="px-3 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                {SOURCE_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {/* Ask button */}
              <button
                type="submit"
                disabled={loading || !question.trim()}
                className="px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                {loading ? (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                )}
                Ask
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
